import { Alert, Badge, Group, Paper, Tabs, Text, Title } from "@mantine/core";
import { IconListCheck, IconPlus } from "@tabler/icons-react";
import * as _ from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import {
  generatePath,
  Route,
  Routes,
  useNavigate,
  useParams
} from "react-router";
import { usePersons } from "../../hooks/usePersons";
import { useProject } from "../../hooks/useProject";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { useSystemUsers } from "../../hooks/useSystemUsers";
import { IconButton } from "../../mantine/components/IconButton/IconButton";
import { AlertPresets } from "../../mantine/mantinePresets";
import { useToast } from "../../mantine/notifications/useToast";
import { ROUTES } from "../../routes";
import { OptiSupportEndpoints } from "../../urls";
import { type Todo, TodoCategory, TodoStatus } from "../../utils/backend-types";
import { hasPremiumSite } from "../../utils/hasPremiumSite";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { OptiSupportHelpLink } from "../OptiSupportHelpLink/OptiSupportHelpLink";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import { AutoFillTodoModal } from "../Todos/AutoFillTodoModal/AutoFillTodoModal";
import { DeleteTodosButton } from "../Todos/DeleteTodosButton/DeleteTodosButton";
import { useTodos } from "../Todos/hooks/useTodos";
import { NewTodoModal } from "../Todos/NewTodoModal/NewTodoModal";
import { SingleTodo } from "../Todos/SingleTodo";
import { TodoCalendarIntegration } from "../Todos/TodoCalendarIntegration/TodoCalendarIntegration";
import { Mode, type TodoUser } from "../Todos/Todos.types";
import { getDefaultTodoTab } from "../Todos/utils/getDefaultTodoTab";
import { TodoStats } from "./TodoStats/TodoStats";
import "./TodosV2.scss";
import { TodoTable } from "./TodoTable/TodoTable";
import { handleChangeDueDate } from "./utils/handleChangeDueDate";
import { handleChangeOverdue } from "./utils/handleChangeOverdue";
import { handleChangePriority } from "./utils/handleChangePriority";
import { handleChangeResponsible } from "./utils/handleChangeResponsible";
import { handleChangeStatus } from "./utils/handleChangeStatus";

enum TabValues {
  Once = "once",
  Recurring = "recurring"
}

interface TodoTabLabelProps {
  label: string;
  amount?: number;
}

function TodoTab({ label, amount }: TodoTabLabelProps) {
  return (
    <Group>
      <Text>{label}</Text>
      {amount && amount > 0 && <Badge>{amount}</Badge>}
    </Group>
  );
}

interface TodosV2Props {
  variantId: number;
}

function TodosV2({ variantId }: TodosV2Props) {
  const [idSelection, setIdSelection] = useState<Array<number>>([]);
  const [isNewTodoModalOpen, setIsNewTodoModalOpen] = useState(false);
  const [isAutoFillTodoModalOpen, setIsAutoFillTodoModalOpen] = useState(false);

  const { shouldShowStaffView: isStaff } = useShouldShowStaffView();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showErrorToast } = useToast();

  const {
    project,
    isLoading: projectLoading,
    error: projectError
  } = useProject(projectId);
  const users = project?.managers;

  const {
    persons,
    isLoading: personsLoading,
    error: personsError
  } = usePersons({ variantId });

  const {
    siteCategories,
    isLoading: siteCategoriesLoading,
    error: siteCategoriesError
  } = useSiteCategories(projectId || null);
  const isPremium = hasPremiumSite(siteCategories);

  /* controlled tabs since the tab content should only be rendered if selected */
  const [tab, setTab] = useState(
    typeof isPremium === "boolean"
      ? isPremium
        ? TabValues.Once
        : TabValues.Recurring
      : TabValues.Recurring
  );

  useEffect(() => {
    if (isPremium) {
      setTab(isPremium ? TabValues.Once : TabValues.Recurring);
    }
  }, [isPremium]);

  const {
    todos,
    isLoading: todosLoading,
    error: todosError,
    updateTodoResponsible,
    updateTodoPriority,
    updateTodoStatus,
    updateTodoDueDate,
    updateTodoOverdue,
    updateTodoPerson,
    updateDocumentProvided,
    removeTodo,
    removeTodos
  } = useTodos({
    mode: Mode.Normal,
    variantId,
    onUpdateError: (error) => showErrorToast(error)
  });

  const oneTimeTodos =
    todos &&
    todos.filter(
      (todo) =>
        todo.category !== TodoCategory.RegulatoryDuty ||
        !todo.regulatoryDuty?.recurring
    );
  const openOneTimeTodosAmount = oneTimeTodos?.filter(
    (todo) => todo.status === TodoStatus.Open
  ).length;
  const recurringTodos =
    todos &&
    todos.filter(
      (todo) =>
        todo.category === TodoCategory.RegulatoryDuty &&
        todo.regulatoryDuty?.recurring
    );
  const openRecurringTodosAmount = recurringTodos?.filter(
    (todo) => todo.status === TodoStatus.Open
  ).length;

  const {
    isLoading: systemUsersLoading,
    error: systemUsersError,
    data: systemUsers
  } = useSystemUsers();

  // It may happen that a system user is also a project manager
  const uniqueUsersWithSystemUsers = useMemo(() => {
    const markedSystemUsers =
      systemUsers &&
      systemUsers.map<TodoUser>((user) => {
        return { ...user, isSystemUser: true };
      });

    return _.uniqBy<TodoUser>(
      users && markedSystemUsers && users.concat(markedSystemUsers),
      "id"
    );
  }, [users, systemUsers]);

  function handleAutoFillTodoModalRefreshRequired() {
    if (projectId) {
      document.location.href = generatePath(ROUTES.todos.entry, {
        projectId: projectId
      });
    }
  }

  function handleTodoSelectionChange(selectedIds: Array<number>) {
    setIdSelection(selectedIds);
  }

  function getSelectedTodos(): Array<Todo> {
    return todos?.filter((todo) => idSelection.includes(todo.id)) || [];
  }

  async function handleDeleteTodo(todo: Todo) {
    await removeTodo(todo).catch((error) => showErrorToast(error));
  }

  async function handleDeleteMultipleTodos(todos: Array<Todo>) {
    await removeTodos(todos).catch((error) => showErrorToast(error));
  }

  const toggleTodoModal = (todo: Todo | false) => {
    if (todo) {
      navigate(`./${todo.id}/${getDefaultTodoTab(todo)}`);
    } else {
      navigate(".");
    }
  };

  // this should never happen unless Todos is used incorrectly from a wrong route
  if (!projectId) {
    return (
      <Alert color={AlertPresets.error.color}>
        Diese URL ist nicht gültig.
      </Alert>
    );
  }

  const ElementSingleTodo = (
    <>
      {todos && (
        <SingleTodo
          mode={Mode.Normal}
          projectId={projectId}
          showTodoStatus={true}
          todos={todos}
          users={uniqueUsersWithSystemUsers}
          onChangeDueDate={(todoId, dueDate) =>
            handleChangeDueDate(todos, updateTodoDueDate, todoId, dueDate)
          }
          onChangeOverdue={(todoId, overdue) =>
            handleChangeOverdue(todos, updateTodoOverdue, todoId, overdue)
          }
          onChangePerson={(todoId, personId) =>
            updateTodoPerson(todoId, personId)
          }
          onChangePriority={(todoId, priority) =>
            handleChangePriority(todos, updateTodoPriority, todoId, priority)
          }
          onChangeResponsible={(todoId, responsibleId) =>
            handleChangeResponsible(
              todos,
              updateTodoResponsible,
              todoId,
              responsibleId
            )
          }
          onChangeStatus={(todoId, status) =>
            handleChangeStatus(todos, updateTodoStatus, todoId, status)
          }
          onChangeTodoStatus={updateDocumentProvided}
          onToggleTodoModal={() => toggleTodoModal(false)}
        />
      )}
    </>
  );

  return (
    <div className="TodosV2">
      <LoadOrError
        error={
          projectError ||
          personsError ||
          todosError ||
          siteCategoriesError ||
          systemUsersError
        }
        loading={
          projectLoading ||
          personsLoading ||
          todosLoading ||
          siteCategoriesLoading ||
          systemUsersLoading
        }
      >
        {project &&
          persons &&
          todos &&
          oneTimeTodos &&
          openOneTimeTodosAmount !== undefined &&
          recurringTodos &&
          openRecurringTodosAmount !== undefined &&
          isPremium !== undefined && (
            <>
              <Paper>
                <Group justify="space-between" variant="paper-header">
                  <Title variant="paper-header-font">Aufgaben</Title>
                  {isStaff && (
                    <Group gap="sm">
                      <IconButton
                        color="primary"
                        icon={IconPlus}
                        onClick={() => setIsNewTodoModalOpen(true)}
                      >
                        Neue Aufgabe
                      </IconButton>
                      <IconButton
                        color="neutral"
                        icon={IconListCheck}
                        variant="outline"
                        onClick={() => setIsAutoFillTodoModalOpen(true)}
                      >
                        Liste aktualisieren
                      </IconButton>
                      <TodoCalendarIntegration variantId={variantId} />
                      <DeleteTodosButton
                        todos={getSelectedTodos()}
                        onDeleteMultipleTodos={handleDeleteMultipleTodos}
                        onDeleteTodo={handleDeleteTodo}
                      />
                    </Group>
                  )}
                </Group>
                <TodoStats variantId={variantId} />
                <Tabs
                  className="tabs"
                  value={tab}
                  onChange={(newTab) => {
                    setTab(newTab as TabValues);
                    setIdSelection([]);
                  }}
                >
                  <Tabs.List className="tabs-list">
                    <Tabs.Tab value={TabValues.Once}>
                      <TodoTab
                        amount={openOneTimeTodosAmount}
                        label="Einmalig"
                      />
                    </Tabs.Tab>
                    <Tabs.Tab value={TabValues.Recurring}>
                      <TodoTab
                        amount={openRecurringTodosAmount}
                        label="Wiederkehrend"
                      />
                    </Tabs.Tab>
                    <OptiSupportHelpLink
                      optiSupportEndpoint={
                        OptiSupportEndpoints.RegulatorischeAufgaben
                      }
                      text="Was sind Aufgaben?"
                    />
                  </Tabs.List>

                  {tab === TabValues.Once && (
                    <Tabs.Panel value={TabValues.Once}>
                      <TodoTable
                        isPremium={isPremium}
                        mode={TabValues.Once}
                        projectId={projectId}
                        variantId={variantId}
                        onRowSelectionChange={handleTodoSelectionChange}
                      />
                    </Tabs.Panel>
                  )}

                  {tab === TabValues.Recurring && (
                    <Tabs.Panel value={TabValues.Recurring}>
                      <TodoTable
                        isPremium={isPremium}
                        mode={TabValues.Recurring}
                        projectId={projectId}
                        variantId={variantId}
                        onRowSelectionChange={handleTodoSelectionChange}
                      />
                    </Tabs.Panel>
                  )}
                </Tabs>
              </Paper>

              <NewTodoModal
                basePath={ROUTES.todos.entry}
                isOpen={isNewTodoModalOpen}
                mode={Mode.Normal}
                persons={persons}
                projectId={projectId}
                users={project.managers}
                variantId={variantId}
                onToggle={() => setIsNewTodoModalOpen(false)}
              />
              <AutoFillTodoModal
                isOpen={isAutoFillTodoModalOpen}
                users={project.managers}
                variantId={variantId}
                onRequireTodoRefresh={handleAutoFillTodoModalRefreshRequired}
                onToggle={() => setIsAutoFillTodoModalOpen(false)}
              />
              <Routes>
                <Route element={ElementSingleTodo} path=":todoId">
                  <Route element={ElementSingleTodo} index />
                  <Route element={ElementSingleTodo} path="*" />
                </Route>
              </Routes>
            </>
          )}
      </LoadOrError>
    </div>
  );
}

export { TabValues, TodosV2, TodosV2Props };
